import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  clearAllNotifications,
  deleteNotification,
  markAllNotificationsRead,
  markNotificationRead,
  updateNotificationPreferences,
  type NotificationPreferencesDto,
} from '@api/notifications';
import { notificationKeys } from './notificationQueryKeys';

/**
 * Mutations that change notification state (mark read, clear, update prefs).
 * Invalidates the relevant React Query caches after a successful write so the
 * profile-menu unread badge / notification list / settings page all stay in sync.
 */
export function useNotificationActions() {
  const queryClient = useQueryClient();

  const invalidateLists = () => {
    queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
    queryClient.invalidateQueries({ queryKey: notificationKeys.lists() });
  };

  const markRead = useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onSuccess: invalidateLists,
  });

  const markAllRead = useMutation({
    mutationFn: () => markAllNotificationsRead(),
    onSuccess: invalidateLists,
  });

  const clearOne = useMutation({
    mutationFn: (id: string) => deleteNotification(id),
    onSuccess: invalidateLists,
  });

  const clearAll = useMutation({
    mutationFn: () => clearAllNotifications(),
    onSuccess: invalidateLists,
  });

  const updatePreferences = useMutation({
    mutationFn: (prefs: NotificationPreferencesDto) => updateNotificationPreferences(prefs),
    onMutate: async (newPrefs) => {
      await queryClient.cancelQueries({ queryKey: notificationKeys.preferences() });
      const previous = queryClient.getQueryData<NotificationPreferencesDto>(
        notificationKeys.preferences(),
      );
      queryClient.setQueryData(notificationKeys.preferences(), newPrefs);
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(notificationKeys.preferences(), context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.preferences() });
    },
  });

  return { markRead, markAllRead, clearOne, clearAll, updatePreferences };
}
